// src/components/dashboard/EventDetails/WaterDepthVisual.jsx
// 改进版水深可视化 - 支持新的NWS阈值标准和信息面板

import React, { useState } from 'react'; // 🔧 添加useState导入
import { Droplets, Info, ExternalLink } from 'lucide-react';
import { 
  getThresholdConfig, 
  getAllThresholds, 
  getThresholdBreakpoints,
  isThresholdBreakpoint,
  THRESHOLD_METADATA,
  formatThresholdRange
} from '../../../utils/floodThresholds';

const WaterDepthVisual = ({ 
  waterDepthInches, 
  severity = 'minor',  // 🔧 改为新的默认值
  showTireChart = true,
  showDetailedStats = false,
  isInteractive = false,
  selectedPoint = null,
  hoveredPointData = null,  // 新增
isHovering = false        // 新增
}) => {
  const [showThresholdInfo, setShowThresholdInfo] = useState(false); // 🔧 新增状态
  // 🔧 计算当前显示的水深（支持悬停交互）
const currentWaterDepth = hoveredPointData ? hoveredPointData.depth : waterDepthInches;
const currentSeverity = hoveredPointData ? hoveredPointData.severity : severity;
  
  // 计算水位百分比（基于24英寸最大值）
  const maxDepth = 24;

const depthPercentage = Math.min(100, Math.max(0, (currentWaterDepth / maxDepth) * 100));
const currentThreshold = getThresholdConfig(currentSeverity);

  const allThresholds = getAllThresholds();
  const thresholdBreakpoints = getThresholdBreakpoints();

  return (
    <div className="water-depth-container">
      {/* 🔧 新增：洪水阈值信息说明面板 */}
      <div className="p-3 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-2 border-blue-200 mb-4 relative">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-semibold text-blue-900">
                Maximun Water Depth
              </h3>
              <button
                onClick={() => setShowThresholdInfo(!showThresholdInfo)}
                className="p-1 hover:bg-blue-200 rounded-full transition-colors"
                title="View NWS threshold standards"
              >
                <Info className="h-4 w-4 text-blue-600" />
              </button>
              {isInteractive && selectedPoint !== null && (
                <span className="text-xs text-blue-600">
                  (Point {selectedPoint + 1})
                </span>
              )}
            </div>
            
            <div className="flex items-center gap-3">
              <div className="text-2xl font-bold text-blue-800">
                {currentWaterDepth}"
              </div>
              <div className="flex items-center gap-1">
                <span className="text-lg">{currentThreshold.icon}</span>
                <span className="text-sm font-medium text-blue-700">
                  {currentThreshold.label}
                </span>
              </div>
            </div>
            
            {showDetailedStats && (
              <p className="text-xs text-blue-600 mt-1">
                Range: {formatThresholdRange(currentThreshold)} • {currentThreshold.vehicleImpact}
              </p>
            )}
          </div>
          
          <div className="text-right">
            <Droplets className="h-8 w-8 text-blue-500 mb-2" />
            <div className="text-xs text-blue-600 text-right">
              {currentThreshold.description}
            </div>
          </div>
        </div>

        {/* 🔧 新增：NWS阈值标准说明面板 */}
        {showThresholdInfo && (
          <div className="absolute top-full left-0 right-0 mt-2 p-4 bg-white rounded-lg shadow-lg border-2 border-blue-200 z-50">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                📊 Flood Depth Thresholds
                <span className="text-xs font-normal text-gray-500">
                  ({THRESHOLD_METADATA.standard})
                </span>
              </h4>
              <a 
                href={THRESHOLD_METADATA.reference}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
              >
                NWS Standard <ExternalLink className="h-3 w-3" />
              </a>
            </div>
            
            <div className="space-y-2 text-sm">
              {allThresholds.map(threshold => (
                <div 
                  key={threshold.severity}
                  className={`flex items-center gap-3 p-2 rounded transition-colors ${
                    threshold.severity === currentThreshold.severity 
                      ? 'bg-blue-50 border border-blue-200' 
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2 flex-1">
                    <span className="text-lg">{threshold.icon}</span>
                    <div>
                      <div className="font-medium">{threshold.label}</div>
                      <div className="text-xs text-gray-600">
                        {formatThresholdRange(threshold)}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 text-right max-w-32">
                    <div className="font-medium">{threshold.vehicleImpact}</div>
                    <div className="text-gray-400 leading-tight">{threshold.description}</div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500">
              <p className="font-medium mb-1">
                📋 {THRESHOLD_METADATA.description}
              </p>
              <div className="space-y-1">
                {THRESHOLD_METADATA.notes.map((note, index) => (
                  <p key={index}>• {note}</p>
                ))}
              </div>
              <p className="mt-2 text-gray-400">
                Last updated: {THRESHOLD_METADATA.lastUpdated}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* 轮胎可视化图表 - 使用新的NWS阈值标准 */}
      {showTireChart && (
        <div className="tire-chart-container">
          <div 
            className="relative bg-gray-100 rounded-xl border-2 border-gray-300 overflow-hidden"
            style={{ width: '200px', height: '200px', margin: '0 auto' }}
          >
            {/* 背景轮胎图片 */}
            <img
              src="/images/tire-depth.png"
              alt="Tire depth reference"
              className="absolute inset-0 w-full h-full object-contain z-10"
              style={{ 
                filter: 'contrast(1.1) brightness(0.95)',
                mixBlendMode: 'multiply'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'block';
              }}
            />
            
            {/* 备用轮胎形状 */}
            <div 
              className="absolute inset-0 hidden z-10"
              style={{
                background: 'radial-gradient(circle, #2d3748 35%, #4a5568 36%, #4a5568 65%, #2d3748 66%)',
                borderRadius: '50%'
              }}
            />

            {/* 🔧 水位层 - 使用新的阈值颜色标准 */}
            <div 
              className="absolute bottom-0 left-0 right-0 z-20 transition-all duration-500 ease-out"
              style={{
                height: `${depthPercentage}%`,
                backgroundColor: currentThreshold.color + 'AA', // 添加透明度
                borderTop: `2px solid ${currentThreshold.borderColor}`,
                borderRadius: depthPercentage > 95 ? '50%' : '0 0 50% 50%'
              }}
            >
              {/* 水波纹效果 */}
              <div 
                className="absolute top-0 left-0 right-0 h-1 opacity-60"
                style={{
                  background: `linear-gradient(90deg, 
                    transparent 0%, 
                    ${currentThreshold.borderColor} 25%, 
                    transparent 50%, 
                    ${currentThreshold.borderColor} 75%, 
                    transparent 100%)`,
                  animation: 'wave 2s ease-in-out infinite'
                }}
              />
            </div>

            {/* 🔧 阈值分界线和刻度线 - 显示6"和12"的NWS分界点 */}
            {showDetailedStats && (
              <div className="absolute inset-0 z-30">
                {/* 常规刻度线 */}
                {[6, 12, 18, 24].map(depth => {
                  const percentage = (depth / maxDepth) * 100;
                  const isThreshold = isThresholdBreakpoint(depth);
                  const breakpoint = thresholdBreakpoints.find(bp => bp.depth === depth);
                  
                  return (
                    <div
                      key={depth}
                      className="absolute left-0 right-0 flex items-center"
                      style={{ bottom: `${percentage}%` }}
                    >
                      {/* 分界线 */}
                      <div 
                        className={`h-0.5 opacity-80 ${
                          isThreshold ? 'w-full' : 'w-4'
                        }`}
                        style={{ 
                          backgroundColor: isThreshold ? breakpoint?.color : '#6b7280'
                        }}
                      />
                      
                      {/* 刻度标签 */}
                      <span 
                        className={`text-xs font-medium ml-2 px-1 rounded transition-all ${
                          isThreshold 
                            ? 'bg-white text-gray-800 border shadow-sm font-semibold' 
                            : 'bg-white text-gray-600'
                        }`}
                        style={{ 
                          fontSize: '10px',
                          borderColor: isThreshold ? breakpoint?.color : 'transparent'
                        }}
                      >
                        {depth}"
                        {isThreshold && (
                          <div className="text-xs font-normal leading-tight mt-0.5">
                            {breakpoint?.label}
                          </div>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* 当前水位指示器 */}
            {depthPercentage > 5 && (
              <div 
                className="absolute left-0 right-0 z-40 flex items-center"
                style={{ bottom: `${depthPercentage}%` }}
              >
                <div className="w-full h-0.5 border-t-2 border-dashed border-white opacity-90" />
                <div 
                  className="absolute right-2 text-xs font-bold text-white bg-black bg-opacity-60 px-2 py-1 rounded flex items-center gap-1"
                  style={{ transform: 'translateY(-50%)' }}
                >
                  {currentWaterDepth}" {currentThreshold.icon}
                </div>
              </div>
            )}
          </div>
          
          {/* 图表说明 */}
          <p className="text-center text-xs text-gray-600 mt-3 font-medium">
            Water depth: {currentWaterDepth}" ({currentThreshold.label})
{isHovering && (
  <span className="text-blue-600 ml-2">📍 Live Preview</span>
)}
            {showDetailedStats && (
              <>
                <br />
                <span className="text-gray-500">{currentThreshold.vehicleImpact}</span>
              </>
            )}
          </p>
        </div>
      )}

      {/* 🔧 NWS标准深度等级指示器 */}
      {showDetailedStats && (
        <div className="flex justify-center flex-wrap gap-3 mt-4 text-xs">
          {allThresholds.map(threshold => (
            <div 
              key={threshold.severity} 
              className={`flex items-center space-x-1 px-2 py-1 rounded transition-colors ${
                threshold.severity === currentThreshold.severity
                  ? 'bg-blue-50 border border-blue-200'
                  : ''
              }`}
            >
              <div 
                className="w-3 h-3 rounded-full border"
                style={{ 
                  backgroundColor: threshold.color,
                  borderColor: threshold.borderColor
                }}
              />
              <span className="font-medium">
                {threshold.shortLabel} ({formatThresholdRange(threshold)})
              </span>
            </div>
          ))}
        </div>
      )}

      <style jsx>{`
        @keyframes wave {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(10px); }
        }
      `}</style>
    </div>
  );
};

// 简化版本的水深显示，用于卡片中
export const WaterDepthSimple = ({ waterDepthInches, severity }) => {
  return (
    <WaterDepthVisual 
      waterDepthInches={waterDepthInches}
      severity={severity}
      showTireChart={false}
      showDetailedStats={false}
    />
  );
};

export default WaterDepthVisual;